import {
  useLazyGetDashboardCostByCountyQuery,
  useLazyGetDashboardHeatMapTableQuery,
  useLazyGetDashboardIncompleteRideTableQuery,
  useLazyGetDashboardLSIncompleteRideQuery,
  useLazyGetDashboardLSIntervalQuery,
  useLazyGetDashboardLSRoutineQuery,
  useLazyGetDashboardOnTimePerformanceTableQuery,
  useLazyGetDashboardRenderingServiceQuery,
  useLazyGetDashboardRideAverageMilesQuery,
  useLazyGetDashboardRideMemberTableQuery,
  useLazyGetDashboardRideOtpQuery,
  useLazyGetDashboardRideTableByCountyQuery,
  useLazyGetDashboardRideTableStatusQuery,
  useLazyGetRideTableReportDataQuery,
} from '@src/store/rtk/dashboard-tables';
import { getRideReportSelectableColumns } from '../ride-reports/helpers';
import { useAppSelector } from '@src/store/hooks';
import { selectGlobal } from '@src/store/slices/global/global';
import { useLocation } from 'react-router-dom';
import {
  ReportType,
  RIDE_REPORT_TYPES,
} from '@component/LightVersion/Reports/ride-reports/constants';
import { useGetRangeQuery } from '@component/LightVersion/Reports/hooks/useGetRangeQuery';

export type DashboardTableColumns = {
  all: any[];
  visible: any[];
};

export const REPORT_VISIBLE_COLUMNS: Record<ReportType, string[]> = {
  [RIDE_REPORT_TYPES.RIDES_REPORT]: [
    'fullName',
    'memberIdNumber',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceType',
    'pickupAddress',
    'dropOffAddress',
    'contractor',
    'rideStatus',
    'statusGroup',
    'cost',
    'onTimeStatus',
  ],
  [RIDE_REPORT_TYPES.INCOMPLETE_REPORT]: [
    'fullName',
    'memberIdNumber',
    'confirmationCode',
    'dateOfService',
    'statusChangedDate',
    'levelOfService',
    'serviceType',
    'rideStatus',
  ],
  [RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'confirmationCode',
    'dateOfService',
    'statusChangedDate',
    'levelOfService',
    'serviceType',
    'serviceTypeCategory',
  ],
  [RIDE_REPORT_TYPES.COST_BY_COUNTY]: [
    'pickupAddressCounty',
    'firstDateOfService',
    'lastDateOfService',
    'countRide',
    'totalRideCost',
    'averageRideCost',
  ],
  [RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE]: [
    'firstDateOfService',
    'lastDateOfService',
    'countOnTimeTrips',
    'countLateTrips',
    'percentageOnTime',
    'percentageLate',
  ],
  [RIDE_REPORT_TYPES.RENDERING_SERVICE]: [
    'serviceType',
    'serviceTypeCategory',
    'firstDateOfService',
    'lastDateOfService',
    'countRide',
  ],
  [RIDE_REPORT_TYPES.RIDE_LS_INTERVAL]: [
    'firstDateOfService',
    'lastDateOfService',
    'serviceType',
    'serviceTypeCategory',
    'countOnTimeTrips',
    'percentageOnTime',
    'percentageLate',
    'countLateTrips',
  ],
  [RIDE_REPORT_TYPES.RIDE_MEMBER_DATA]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceType',
    'pickupAddress',
    'dropOffAddress',
  ],
  [RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE]: [
    'fullName',
    'memberIdNumber',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceType',
    'willCallIndicator',
    'pickupTime',
    'actualPickupTime',
    'contractor',
    'onTimeStatus',
  ],
  [RIDE_REPORT_TYPES.RIDE_LS_ROUTINE]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceTypeCategory',
    'serviceType',
  ],
  [RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'statusGroup',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceType',
    'statusChangedDate',
  ],
  [RIDE_REPORT_TYPES.RIDE_COUNTY]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'serviceType',
    'pickupAddress',
    'pickupAddressCounty',
    'dropOffAddress',
    'dropOffAddressCounty',
  ],
  [RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'pickupAddress',
    'dropOffAddress',
    'estMiles',
    'actualMiles',
  ],
  [RIDE_REPORT_TYPES.RIDE_HEAT_MAP]: [
    'fullName',
    'memberIdNumber',
    'rideStatus',
    'dateOfService',
    'confirmationCode',
    'levelOfService',
    'pickupTime',
    'actualPickupTime',
    'onTimeStatus',
  ],
  'contractor-top-cost-members': [],
};

export const removeStatuses = [
  'countRide',
  'percentageLate',
  'percentageOnTime',
  'countLateTrips',
  'countOnTimeTrips',
  'lastDateOfService',
  'firstDateOfService',
  'averageRideCost',
];

const buildColumns = (columns: any[], visibleKeys: string[] = []) => {
  return visibleKeys
    .map((key) => {
      const column = columns.find((col) => col.key === key);
      if (column) {
        return {
          ...column,
          show: true,
        };
      }
      return null;
    })
    .filter(Boolean);
};

const buildRideTableColumns = (
  reportType: ReportType,
  visibleKeys: string[] = []
): DashboardTableColumns => {
  const columns = getRideReportSelectableColumns(reportType);

  return {
    all: columns,
    visible: buildColumns(columns, visibleKeys),
  };
};

export const useDashboardTableQueries = () => {
  const { pathname } = useLocation();
  const shouldShowStatusGroup = pathname.includes(
    RIDE_REPORT_TYPES.RIDES_REPORT
  );
  const { dashboardChartQuery, defaultValuesForCharts } =
    useAppSelector(selectGlobal);
  const { rangeQueryParams } = useGetRangeQuery();

  // FIRST_SELECT_DATE
  const [fetchRideReport, rideReportState] =
    useLazyGetRideTableReportDataQuery();

  const [fetchIncompleteReport, incompleteReportState] =
    useLazyGetDashboardIncompleteRideTableQuery();

  const [fetchLSIncompleteData, lsIncompleteState] =
    useLazyGetDashboardLSIncompleteRideQuery();

  const [fetchDashboardCostByCounty, rideCostByCounty] =
    useLazyGetDashboardCostByCountyQuery();

  const [fetchDashboardOtpQuery, rideOtpPerformance] =
    useLazyGetDashboardRideOtpQuery();

  const [fetchDashboardLSInterval, rideLSInterval] =
    useLazyGetDashboardLSIntervalQuery();

  // SECOND_SELECT_DATE
  const [fetchRideMemberData, rideMemberState] =
    useLazyGetDashboardRideMemberTableQuery();

  const [fetchDashboardLSRoutine, rideRoutine] =
    useLazyGetDashboardLSRoutineQuery();

  const [fetchRenderingService, renderingServiceState] =
    useLazyGetDashboardRenderingServiceQuery();

  const [fetchOnTimePerformance, performanceState] =
    useLazyGetDashboardOnTimePerformanceTableQuery();

  const [fetchRideHeatMap, rideHeatMapState] =
    useLazyGetDashboardHeatMapTableQuery();
  const [fetchRideStatus, rideStatusState] =
    useLazyGetDashboardRideTableStatusQuery();
  const [fetchRideCounty, rideCountyState] =
    useLazyGetDashboardRideTableByCountyQuery();
  const [fetchRideAverageMiles, rideAverageState] =
    useLazyGetDashboardRideAverageMilesQuery();

  const queryConfig: Partial<
    Record<
      ReportType,
      {
        trigger: (arg?: any) => void;
        state: {
          data?: any;
          isLoading: boolean;
          isFetching: boolean;
          error?: unknown;
        };
        columns?: DashboardTableColumns;
      }
    >
  > = {
    [RIDE_REPORT_TYPES.RIDES_REPORT]: {
      trigger: (params) =>
        fetchRideReport({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideReportState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDES_REPORT,
        !shouldShowStatusGroup
          ? REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDES_REPORT].filter(
              (el) => el !== 'statusGroup'
            )
          : REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDES_REPORT]
      ),
    },
    [RIDE_REPORT_TYPES.INCOMPLETE_REPORT]: {
      trigger: (params) =>
        fetchIncompleteReport({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: incompleteReportState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.INCOMPLETE_REPORT,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.INCOMPLETE_REPORT]
      ),
    },
    [RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT]: {
      trigger: (params) =>
        fetchLSIncompleteData({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: lsIncompleteState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT]
      ),
    },
    [RIDE_REPORT_TYPES.COST_BY_COUNTY]: {
      trigger: (params) =>
        fetchDashboardCostByCounty({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideCostByCounty,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.COST_BY_COUNTY,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.COST_BY_COUNTY]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE]: {
      trigger: (params) =>
        fetchDashboardOtpQuery({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideOtpPerformance,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE]
      ),
    },
    [RIDE_REPORT_TYPES.RENDERING_SERVICE]: {
      trigger: (params) =>
        fetchRenderingService({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),

      state: renderingServiceState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RENDERING_SERVICE,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RENDERING_SERVICE]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_LS_INTERVAL]: {
      trigger: (params) =>
        fetchDashboardLSInterval({
          ...dashboardChartQuery,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideLSInterval,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_LS_INTERVAL,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_LS_INTERVAL]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_MEMBER_DATA]: {
      trigger: (params) =>
        fetchRideMemberData({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideMemberState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_MEMBER_DATA,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_MEMBER_DATA]
      ),
    },
    [RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE]: {
      trigger: (params) =>
        fetchOnTimePerformance({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: performanceState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_LS_ROUTINE]: {
      trigger: (params) =>
        fetchDashboardLSRoutine({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideRoutine,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_LS_ROUTINE,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_LS_ROUTINE]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW]: {
      trigger: (params) =>
        fetchRideStatus({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideStatusState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_COUNTY]: {
      trigger: (params) =>
        fetchRideCounty({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideCountyState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_COUNTY,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_COUNTY]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES]: {
      trigger: (params) =>
        fetchRideAverageMiles({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideAverageState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES]
      ),
    },
    [RIDE_REPORT_TYPES.RIDE_HEAT_MAP]: {
      trigger: (params) =>
        fetchRideHeatMap({
          ...defaultValuesForCharts,
          ...rangeQueryParams,
          ...params,
        }),
      state: rideHeatMapState,
      columns: buildRideTableColumns(
        RIDE_REPORT_TYPES.RIDE_HEAT_MAP,
        REPORT_VISIBLE_COLUMNS[RIDE_REPORT_TYPES.RIDE_HEAT_MAP]
      ),
    },
  };

  return queryConfig;
};
